import type { CapabilityGrantResponse } from "@kontextwerk/control-web-client"
import {
  PERMISSION_SECTIONS,
  permissionLevelName,
  permissionStateFromGrants,
  type PermissionGridState,
  type PermissionRowConfig,
  type PermissionSectionConfig,
} from "../../../../../_shared/_logic/permission-sections"
import { GENERAL_PERMISSION_SECTION_ID } from "../navigation"

const LABELLED_KEYS: ReadonlySet<string> = new Set(
  PERMISSION_SECTIONS.flatMap((section) => section.rows.map((row) => row.key)),
)

/** A grant the labelled catalog does not know yet still shows its level under its key. */
const unlabelledRow = (grant: CapabilityGrantResponse): PermissionRowConfig => ({
  key: grant.key,
  label: grant.key,
  levelOptions: (["none", "view", "manage"] as const).map((level) => ({
    value: level,
    name: permissionLevelName(level),
  })),
  ...(grant.scope
    ? { scopeOptions: [{ value: grant.scope, name: grant.scope === "own" ? "Eigene" : "Alle" }] }
    : {}),
})

/** Read-only sections of one permissions tab; "Allgemein" also lists unlabelled grants. */
export const roleGrantSections = (
  tab: string,
  grants: readonly CapabilityGrantResponse[],
): PermissionSectionConfig[] => {
  if (tab !== "general") return PERMISSION_SECTIONS.filter((section) => section.id === tab)

  const unlabelled = grants.filter((grant) => !LABELLED_KEYS.has(grant.key))

  return [
    ...PERMISSION_SECTIONS.filter((section) => section.id === GENERAL_PERMISSION_SECTION_ID),
    ...(unlabelled.length
      ? [{ id: "unlabelled", title: "Weitere Berechtigungen", rows: unlabelled.map(unlabelledRow) }]
      : []),
  ]
}

/** Levels and scopes of a stored role; keys without a grant show "Kein Zugriff". */
export const roleGrantState = (grants: readonly CapabilityGrantResponse[]): PermissionGridState => {
  const state = permissionStateFromGrants(grants)

  for (const grant of grants) {
    if (LABELLED_KEYS.has(grant.key)) continue

    state.levels[grant.key] = grant.level
    state.scopes[grant.key] = grant.scope
  }

  return state
}
